import { useEffect, useRef } from 'react';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import type { EventPropertiesMap } from 'contexts/analytics/types';

/**
 * Fires `eventName` once per visit: once when the page mounts with a
 * `viewKey`, and again only if `viewKey` changes (e.g. switching kit or
 * condition). Pass `undefined` until the page has what it needs to report.
 *
 * `trackEvent` is rebuilt whenever the current kit or path changes, so a plain
 * `useEffect` on it reports the same visit more than once.
 */
export const useTrackPageView = <T extends keyof EventPropertiesMap>(
  eventName: T,
  viewKey: string | undefined,
  properties?: EventPropertiesMap[T],
) => {
  const { trackEvent } = useAnalytics();
  const trackedKeyRef = useRef<string>();
  // Read at fire time, so a new properties object each render doesn't re-fire.
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  useEffect(() => {
    if (!viewKey || trackedKeyRef.current === viewKey) return;
    trackedKeyRef.current = viewKey;
    trackEvent(eventName, propertiesRef.current);
  }, [eventName, viewKey, trackEvent]);
};
